/**
 * Cache-bust token for campaign art <img> src when S3 key is overwritten in place.
 * Query string is ignored by S3 but forces the browser to fetch the latest bytes.
 */
export function stripVolatileArtUrlQuery(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete('t');
    parsed.searchParams.delete('_postei_cb');
    const query = parsed.searchParams.toString();
    return `${parsed.origin}${parsed.pathname}${query ? `?${query}` : ''}`;
  } catch {
    const [base] = url.split('?');
    return base || url;
  }
}

export function resolveArtRevisionToken(art = {}) {
  if (art.revision) {
    return String(art.revision);
  }
  const latestEdit = art.editMetadata?.latestEdit;
  if (latestEdit?.createdAt) {
    return latestEdit.createdAt;
  }
  const history = art.editMetadata?.history;
  if (Array.isArray(history) && history.length > 0) {
    const last = history[history.length - 1];
    if (last?.createdAt) {
      return last.createdAt;
    }
  }
  const rawUrl = art.imageUrl || art.url || '';
  const match = String(rawUrl).match(/[?&]t=(\d+)/);
  return match ? match[1] : null;
}

export function normalizeCampaignArtEntry(art = {}) {
  const canonicalUrl = stripVolatileArtUrlQuery(art.imageUrl || art.url || '');
  const revision = resolveArtRevisionToken(art);
  return {
    ...art,
    imageUrl: canonicalUrl,
    url: canonicalUrl,
    ...(revision ? { revision } : {}),
  };
}

export function resolveSessionUpdatedAtToken(sessionDetail) {
  const updatedAt = sessionDetail?.updatedAt ?? sessionDetail?.workflow?.revision;
  if (!updatedAt) {
    return null;
  }
  if (typeof updatedAt === 'string') {
    return updatedAt;
  }
  try {
    return new Date(updatedAt).toISOString();
  } catch {
    return null;
  }
}

export function withArtDisplayCacheBust(url, options = {}) {
  const {
    renderKey = 0,
    revision = null,
    sessionUpdatedAt = null,
  } = options;

  if (!url || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith('data:')) {
    return url;
  }
  if (url.includes('X-Amz-') || url.includes('Signature=')) {
    return url;
  }

  const cacheToken =
    revision ||
    (renderKey > 0 ? String(renderKey) : null) ||
    sessionUpdatedAt;

  if (!cacheToken) {
    return url;
  }

  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_postei_cb=${encodeURIComponent(cacheToken)}`;
}
